import { useEffect } from 'react';
import { IonApp } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Redirect, Route, Switch } from 'react-router-dom';
import type { Role } from '@tcrd/shared';
import { Spinner } from '@tcrd/ui';
import { homeFor, useAuth } from './stores/auth';
import { ApiError } from './lib/api';
import { LandingPage } from './features/landing/LandingPage';
import { LoginPage } from './features/auth/LoginPage';
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage';
import { PublicTrackPage } from './features/landing/PublicTrackPage';
import { InfoPage } from './features/landing/InfoPage';
import { RegisterPage } from './features/auth/RegisterPage';
import { CustomerShell } from './features/customer/CustomerShell';
import { DriverShell } from './features/driver/DriverShell';
import { AdminShell } from './features/admin/AdminShell';
import { LegalPage } from './features/landing/LegalPage';
import { PublicQuotePage } from './features/landing/PublicQuotePage';
import { NativeBridge } from './features/native/NativeBridge';
import { RealtimeBridge } from './features/native/RealtimeBridge';
import { NewRequestAlert } from './features/driver/NewRequestAlert';
import { CustomerEventAlert } from './features/customer/CustomerEventAlert';
import { PhoneGate } from './features/auth/PhoneGate';
import { RideCheckModal } from './features/safety/RideCheckModal';
import { ReferralModal } from './features/safety/Referral';
import { cleanupRecordings } from './lib/trip-recorder';

// Grabaciones de viajes guardadas en el teléfono: se borran a los 7 días.
void cleanupRecordings();

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (count, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
    mutations: { retry: false },
  },
});

function Protected({ roles, children }: { roles: Role[]; children: React.ReactNode }) {
  const { status, user, hasRole } = useAuth();
  if (status === 'loading') return <Spinner label="Cargando tu sesión…" />;
  if (status === 'anonymous') return <Redirect to={`/login?next=${encodeURIComponent(location.pathname)}`} />;
  if (!hasRole(...roles)) return <Redirect to={homeFor(user)} />;
  return <>{children}</>;
}

export function App() {
  const bootstrap = useAuth((s) => s.bootstrap);
  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  return (
    <QueryClientProvider client={queryClient}>
      <IonApp>
        <IonReactRouter>
          <NativeBridge />
          <RealtimeBridge />
          <NewRequestAlert />
          <CustomerEventAlert />
          <PhoneGate />
          <RideCheckModal />
          <ReferralModal />
          {/* Rutas de nivel superior con Switch; cada área usa su propio IonRouterOutlet/IonTabs. */}
          <Switch>
              <Route exact path="/" component={LandingPage} />
              <Route exact path="/c/:code" component={LandingPage} />
              <Route exact path="/login" component={LoginPage} />
              <Route exact path="/register" component={RegisterPage} />
              <Route exact path="/forgot-password" component={ForgotPasswordPage} />
              <Route exact path="/legal/:doc" component={LegalPage} />
              <Route exact path="/info/:doc" component={InfoPage} />
              <Route exact path="/q/:token" component={PublicQuotePage} />
              <Route exact path="/t/:token" component={PublicTrackPage} />
              <Route path="/app">
                <Protected roles={['CUSTOMER']}>
                  <CustomerShell />
                </Protected>
              </Route>
              <Route path="/driver">
                <Protected roles={['DRIVER', 'TENANT_ADMIN']}>
                  <DriverShell />
                </Protected>
              </Route>
              <Route path="/admin">
                <Protected roles={['SUPER_ADMIN']}>
                  <AdminShell />
                </Protected>
              </Route>
              <Redirect to="/" />
          </Switch>
        </IonReactRouter>
      </IonApp>
    </QueryClientProvider>
  );
}
